import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { get } from '@/utils/api';
import { AuthContext } from '@/contexts/AuthContext';
import { LessonsList } from './lessons';

const RELEASE_RECHECK_BUFFER_MS = 5000;

// Always back to the hub, not history: children also land here from the menu and from a finished quiz.
const BackToHub = () => (
  <Link
    to="/lets-learn"
    className="flex w-fit items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#2F1E6B] shadow-sm transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300"
  >
    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
    Home Learning
  </Link>
);

const CoreSkillsList = () => {
  const [lesson, setLesson] = useState(null);
  const [topic, setTopic] = useState(null);
  const [canAccess, setCanAccess] = useState(false);
  const [lessonAccessState, setLessonAccessState] = useState('pre_quiz_pending');
  const [releaseDate, setReleaseDate] = useState(null);
  const [completionDate, setCompletionDate] = useState(null);
  const [releaseHours, setReleaseHours] = useState(48);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { loggedInChild, updateLoggedInChild, user } = useContext(AuthContext);

  const fetchLesson = async () => {
    try {
      const res = await get(`/topics/core-skills/current/${loggedInChild._id}`);
      setLesson(res.lesson);
      setTopic(res.topic);
      setCanAccess(res.canAccess);
      setLessonAccessState(res.lessonAccessState ?? 'pre_quiz_pending');
      setReleaseDate(res.releaseDate);
      setCompletionDate(res.completionDate);
      setReleaseHours(res.releaseHours ?? 48);

      // update the child's current lesson in context if needed
      if (res.lesson && loggedInChild.coreSkillsCurrentLessonId !== res.lesson._id) {
        // Assuming you have a method to update the loggedInChild in context
        updateLoggedInChild({ ...loggedInChild, coreSkillsCurrentLessonId: res.lesson._id });
      }
    } catch (err) {
      console.log({err});
      setError('Could not load Core Skills lesson.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLesson();
  }, []);

  // While the "next lesson available by" message shows, re-fetch just after that
  // time; the API releases the lesson on read, so it appears without a reload.
  useEffect(() => {
    if (lessonAccessState !== 'complete_full' && lessonAccessState !== 'streak_cap_reached') return undefined;
    const dueAt = new Date(completionDate || releaseDate).getTime() + releaseHours * 60 * 60 * 1000;
    const delay = dueAt - Date.now();
    if (!(delay > 0)) return undefined;
    const timer = setTimeout(fetchLesson, delay + RELEASE_RECHECK_BUFFER_MS);
    return () => clearTimeout(timer);
  }, [lessonAccessState, completionDate, releaseDate, releaseHours]);

  const BareRender = ({children}) => {
    return (
      <div className='container mx-auto m-16'>
        <BackToHub />
        <div className='mt-4'>{children}</div>
      </div>
    );
  };

  if (loading) return <BareRender>Loading...</BareRender>;
  if (!loggedInChild) return <BareRender>Please log in, using the <a href="/children-dashboard"><strong><u>children dashboard</u></strong></a>  tab to view your core skills lesson.</BareRender>;
  if (error) return <BareRender>{error}</BareRender>;
  if (!lesson) {
    // On a free trial, Core Skills lessons aren't available yet — explain why
    // rather than showing a bare "no lesson" message.
    if (user?.isOnTrial) {
      return (
        <BareRender>
          {`Core Skills lessons become available to paying customers — they'll unlock for ${loggedInChild.name} once your free trial ends and your subscription begins.`}
        </BareRender>
      );
    }
    return <BareRender>{`${loggedInChild.name} has no Core Skills lesson available.`}</BareRender>;
  }

  return (
    <div className="container mx-auto p-4 min-h-screen flex flex-col my-8">
      <BackToHub />
      <h1 className="text-2xl font-semibold text-foreground my-4">Core Skills Lesson</h1>

      <LessonsList topic={topic} lessons={[lesson]} canAccess={canAccess} lessonAccessState={lessonAccessState} releaseDate={releaseDate} completionDate={completionDate} releaseHours={releaseHours} isCoreSkill />
    </div>
  );
};

export default CoreSkillsList;